/**
 * Helpers to keep list search/filters in the URL across list → detail → Back
 * (same tab, new tab, and deep-link fallbacks).
 */

/**
 * Build a query suffix from a plain object of stringable values.
 * Omits null/undefined/empty-string entries. Returns '' or '?key=value&…'.
 */
export function buildQuerySuffix(params = {}) {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    p.set(key, String(value));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * Join a list path with a location.search (or raw query string).
 * `search` may be '', '?', '?a=1', or 'a=1'.
 */
export function withListQuery(path, search = '') {
  if (!path) return search || '';
  const [pathname, existingQuery = ''] = String(path).split('?');
  const incoming = String(search || '').replace(/^\?/, '');
  const query = incoming || existingQuery;
  return query ? `${pathname}?${query}` : pathname;
}

/**
 * Keep only the listed query keys from a search string (drops detail-only params like `comment`).
 */
export function pickQuerySuffix(search = '', keys = []) {
  const src = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const params = {};
  keys.forEach((key) => {
    if (!src.has(key)) return;
    const value = src.get(key);
    if (value === null || value === '') return;
    params[key] = value;
  });
  return buildQuerySuffix(params);
}

/** Same-app relative path only (blocks open redirects). */
export function isSafeReturnTo(path) {
  if (!path || typeof path !== 'string') return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.includes('://')) return false;
  return true;
}

/**
 * Prefer an explicit `returnTo` query (e.g. Work Register → detail → Back).
 * Falls back to `defaultFallback` when missing/unsafe.
 */
export function getReturnToFallback(search = '', defaultFallback = '') {
  const src = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const returnTo = src.get('returnTo');
  if (isSafeReturnTo(returnTo)) return returnTo;
  return defaultFallback;
}

/** Append `returnTo` onto a target path that may already have a query string. */
export function withReturnTo(targetPath, returnPath) {
  if (!targetPath || !isSafeReturnTo(returnPath)) return targetPath || '';
  const [pathname, existingQuery = ''] = String(targetPath).split('?');
  const params = new URLSearchParams(existingQuery);
  params.set('returnTo', returnPath);
  const s = params.toString();
  return s ? `${pathname}?${s}` : pathname;
}
